import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { EditorialRails } from "@/components/EditorialRails";
import { TodayInCity } from "@/components/TodayInCity";
import { useCity } from "@/contexts/CityContext";
import { useWaitlist } from "@/contexts/WaitlistContext";
import { useDocumentMeta, useJsonLd } from "@/hooks/useSeo";
import { fetchHeroImages, heroIndexForToday, heroSrcSet } from "@/lib/heroImages";
import { fetchEditorialRails, type EditorialRail } from "@/lib/queries";
import { homeTitle, ldGraph, organizationLd, PRICE_EUR, webSiteLd } from "@/seo/shared";

export default function Landing() {
  const { t } = useTranslation();
  useDocumentMeta({
    fullTitle: homeTitle(t("seo.home.title")),
    description: t("seo.home.description"),
    path: "/",
  });
  useJsonLd(ldGraph(organizationLd(), webSiteLd()));

  // Los rails se filtran por la ciudad elegida (el payload cacheado es único;
  // el corte por ciudad se hace en cliente dentro de fetchEditorialRails).
  const { city } = useCity();
  const { data: rails = [], isLoading: railsLoading } = useQuery<
    EditorialRail[]
  >({
    queryKey: ["landing", "editorial-rails", city ?? "all"],
    queryFn: () => fetchEditorialRails(city),
    staleTime: 60_000,
  });

  return (
    <>
      <Hero />
      <TodayInCity />
      <EditorialRails rails={rails} loading={railsLoading} />
      <Membership />
    </>
  );
}

/* -------------------------------------------------------------------- */
/*  Membership band                                                     */
/* -------------------------------------------------------------------- */

function Membership() {
  const { t } = useTranslation();
  const { openWaitlist } = useWaitlist();
  return (
    <section className="bg-rc-lime">
      <div className="max-w-6xl px-6 md:px-14 py-20 md:py-28">
        <h2 className="font-display text-5xl md:text-8xl uppercase tracking-[-0.04em] leading-[0.85] text-rc-navy">
          {t("landing.membership.title", { price: PRICE_EUR })}
        </h2>
        <p className="mt-6 text-rc-navy leading-[1.6] text-base md:text-lg max-w-xl">
          {t("landing.membership.body")}
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => openWaitlist("landing")}
            className="data-voice inline-flex items-center justify-center rounded-full bg-rc-navy px-8 py-4 text-sm font-bold text-rc-lime transition-[transform,box-shadow] duration-200 ease-spring hover:-translate-x-[3px] hover:-translate-y-[3px] hover:shadow-hard-sm-light active:translate-x-0 active:translate-y-0 active:shadow-none active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet focus-visible:ring-offset-2"
          >
            {t("landing.membership.cta")}
          </button>
          <Link
            to="/suscripcion"
            className="data-voice inline-flex items-center justify-center rounded-full border-2 border-rc-navy px-8 py-4 text-sm font-bold text-rc-navy transition-colors duration-200 hover:bg-white active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet"
          >
            {t("landing.membership.link")}
          </Link>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------- */
/*  Hero                                                                 */
/* -------------------------------------------------------------------- */

function Hero() {
  const { t } = useTranslation();
  const { data: heroImages = [] } = useQuery<string[]>({
    queryKey: ["landing", "hero-images"],
    queryFn: fetchHeroImages,
    staleTime: 5 * 60_000,
  });

  // La primera foto es fija por día (índice = días desde 1970 módulo n), la
  // misma que el prerender precarga desde el HTML; así el LCP no espera al
  // listado del bucket ni a la decodificación (28-10-2026).
  const first = heroIndexForToday(heroImages.length);
  const [current, setCurrent] = useState(first);
  useEffect(() => {
    setCurrent(first);
    if (heroImages.length <= 1) return;
    const id = window.setInterval(
      () => setCurrent((i) => (i + 1) % heroImages.length),
      6000,
    );
    return () => window.clearInterval(id);
  }, [heroImages.length, first]);

  return (
    // Exploración: cartel a dos tonos sobre el morado de marca, titular enorme
    // abajo a la izquierda (no centrado), sin degradados.
    <section className="relative min-h-[100svh] flex items-end overflow-hidden bg-rc-violet text-white">
      {/* Slideshow background: each slide crossfades via opacity. */}
      {/* 29-10-2026 (Lighthouse móvil 67, LCP 6,8 s): antes se montaban las
          cuatro fotos a 1920 px y el móvil las bajaba todas (≈1 MB de más; las
          ocultas cuentan como visibles aunque tengan opacidad 0). Ahora solo
          la que se ve y la siguiente, con srcset por ancho; la primera es la
          que precarga el HTML (mismo srcset, ver scripts/prerender.mjs). */}
      {/* isolate + will-change: la mezcla a dos tonos y el fundido van en su
          propia capa, sin repintar la página al hacer scroll (31-10-2026). La
          capa lleva el morado: sin él, la foto se mezclaba con nada y salía
          gris (31-10-2026, el lima no se leía). */}
      <div className="absolute inset-0 isolate bg-rc-violet">
        {heroImages.map((url, i) =>
          i === current || i === (current + 1) % heroImages.length ? (
            <img
              key={url}
              src={url}
              srcSet={heroSrcSet(url)}
              sizes="100vw"
              alt=""
              aria-hidden="true"
              width={1920}
              height={1080}
              fetchPriority={i === first ? "high" : "low"}
              decoding="async"
              className="duotone duotone-hero absolute inset-0 w-full h-full object-cover will-change-[opacity]"
              style={{
                opacity: i === current ? 1 : 0,
                transition: "opacity 1400ms ease-in-out",
              }}
            />
          ) : null
        )}
      </div>
      <div className="relative z-10 w-full px-6 pb-14 pt-32 md:px-14 md:pb-20">
        <h1 className="max-w-6xl font-display text-[3.25rem] sm:text-[4.5rem] md:text-[6rem] lg:text-[7.5rem] uppercase tracking-[-0.04em] leading-[0.85] text-rc-lime">
          {t("landing.hero.title")}
        </h1>
        {/* Una línea que diga lo que es, sin adjetivos ni cifras (29-10-2026,
            usuario): «curación» no era real (entra toda la cartelera de los
            cines con los que trabajamos) y las cifras de la semana hacían el
            hero aparatoso; siguen en /{ciudad} y en el bloque de suscripción. */}
        <p className="data-voice mt-6 max-w-xl text-xs sm:text-sm font-bold leading-[1.6] text-white">
          {t("landing.hero.subtitleLine1")}
          <br />
          {t("landing.hero.subtitleLine2")}
        </p>
        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <Link
            to="/peliculas"
            className="data-voice inline-flex items-center justify-center border-2 border-rc-navy bg-rc-lime text-rc-navy font-bold text-sm px-8 py-4 rounded-full transition-[transform,box-shadow] duration-200 ease-spring hover:-translate-x-[3px] hover:-translate-y-[3px] hover:shadow-hard-sm-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-lime focus-visible:ring-offset-2 focus-visible:ring-offset-rc-violet active:translate-x-0 active:translate-y-0 active:shadow-none active:scale-[0.97]"
          >
            {t("landing.hero.ctaMovies")}
          </Link>
          <Link
            to="/cines"
            className="data-voice inline-flex items-center justify-center border-2 border-white text-white font-bold text-sm px-8 py-4 rounded-full transition-[transform,background-color] duration-200 ease-spring hover:bg-white hover:text-rc-violet focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-rc-violet active:scale-95"
          >
            {t("landing.hero.ctaCinemas")}
          </Link>
        </div>
      </div>

    </section>
  );
}
